import { supabase } from "../supabase";
import { optimizeMaterialImage } from "../helpers/materialImages";
import { APP_RELEASE_ID, APP_VERSION } from "../productEducation/config/appRelease";

export const FEEDBACK_TYPES = Object.freeze(["problem", "suggestion", "other"]);
export const FEEDBACK_BUCKET = "feedback-images";
export const FEEDBACK_MESSAGE_MAX = 4000;

export function feedbackImagePath(organizationId, feedbackId) {
  return `${organizationId}/${feedbackId}/image.jpg`;
}

export function describePlatform(nav = typeof navigator === "undefined" ? {} : navigator) {
  const ua = String(nav.userAgent || "");
  const platform = String(nav.platform || "");
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/mac/i.test(platform)) return "mac";
  if (/win/i.test(platform)) return "windows";
  if (/linux/i.test(platform)) return "linux";
  return "web";
}

export function buildFeedbackRecord({
  feedbackId,
  organizationId,
  feedbackType,
  message,
  contactAllowed,
  sourceRoute,
  sourceScreen,
  nav,
  version = APP_VERSION,
  releaseId = APP_RELEASE_ID,
}) {
  const text = String(message || "").trim();
  if (!FEEDBACK_TYPES.includes(feedbackType)) {
    throw new Error("Choose Problem, Suggestion, or Other.");
  }
  if (!text) {
    throw new Error("Tell us what happened or what you'd like CrewPilot to improve.");
  }
  if (text.length > FEEDBACK_MESSAGE_MAX) {
    throw new Error("Keep the message under 4000 characters.");
  }
  if (!organizationId) {
    throw new Error("CrewPilot couldn't find your organization. Sign in with your company account and try again.");
  }

  const userAgent = String(nav?.userAgent || (typeof navigator === "undefined" ? "" : navigator.userAgent) || "");

  return {
    feedback_id: feedbackId,
    organization_id: organizationId,
    feedback_type: feedbackType,
    message: text,
    contact_allowed: Boolean(contactAllowed),
    source_route: sourceRoute ? String(sourceRoute).slice(0, 500) : null,
    source_screen: sourceScreen ? String(sourceScreen).slice(0, 80) : null,
    app_version: version ? String(version).slice(0, 40) : null,
    app_release_id: releaseId ? String(releaseId).slice(0, 80) : null,
    user_agent: userAgent ? userAgent.slice(0, 500) : null,
    platform: describePlatform(nav),
  };
}

function isDuplicate(error) {
  return error?.code === "23505";
}

/**
 * Persist one feedback row, then optionally store one private image.
 * A failed image does not remove the row. A second insert of the same id
 * is treated as the original row. Feedback does not send email.
 */
export async function submitFeedback({
  feedbackId,
  organizationId,
  feedbackType,
  message,
  contactAllowed,
  sourceRoute,
  sourceScreen,
  file,
  nav,
  client = supabase,
  optimize = optimizeMaterialImage,
}) {
  const row = buildFeedbackRecord({
    feedbackId,
    organizationId,
    feedbackType,
    message,
    contactAllowed,
    sourceRoute,
    sourceScreen,
    nav,
  });

  const { error: insertError } = await client.from("feedback").insert(row);
  if (insertError && !isDuplicate(insertError)) {
    throw insertError;
  }

  let attachmentFailed = false;
  if (file) {
    try {
      const optimized = await optimize(file);
      const path = feedbackImagePath(organizationId, feedbackId);
      const { error: uploadError } = await client.storage.from(FEEDBACK_BUCKET).upload(path, optimized.blob, {
        contentType: "image/jpeg",
        upsert: false,
      });
      if (uploadError && !/already exists|duplicate/i.test(uploadError.message || "")) {
        throw uploadError;
      }
      const { error: attachError } = await client
        .from("feedback")
        .update({ attachment_path: path })
        .eq("feedback_id", feedbackId);
      if (attachError) throw attachError;
    } catch {
      attachmentFailed = true;
    }
  }

  return { feedbackId, attachmentFailed };
}
